import useResizeObserver from "./hooks";
import { type AspectRatioGuide, useSettingsState } from "./settingsStore";
import { classNames } from "./utils";

const ASPECT_RATIO_VALUES: Record<Exclude<AspectRatioGuide, null>, number> = {
  "2.35": 2.35,
  "1.85": 1.85,
  "16:9": 16 / 9,
  "9:16": 9 / 16,
  "4:5": 4 / 5,
  "3:4": 3 / 4,
  "4:3": 4 / 3,
  "1:1": 1,
};

export function AspectRatioGuideOverlay() {
  const { videoGuide, videoGuideMaskEnabled } = useSettingsState();
  const [containerRef, containerDims] = useResizeObserver();

  const containerWidth = containerDims?.width;
  const containerHeight = containerDims?.height;

  const showGuide = videoGuide && containerWidth && containerHeight;

  if (!showGuide) {
    return (
      <div
        ref={containerRef}
        className="pointer-events-none absolute inset-0"
      />
    );
  }

  const targetRatio = ASPECT_RATIO_VALUES[videoGuide];
  const containerRatio = containerWidth / containerHeight;

  let guideWidth: number;
  let guideHeight: number;

  if (targetRatio > containerRatio) {
    guideWidth = containerWidth;
    guideHeight = containerWidth / targetRatio;
  } else {
    guideHeight = containerHeight;
    guideWidth = containerHeight * targetRatio;
  }

  const offsetX = (containerWidth - guideWidth) / 2;
  const offsetY = (containerHeight - guideHeight) / 2;

  const ACTION_SAFE_MARGIN = 0.035;
  const TITLE_SAFE_MARGIN = 0.05;

  const actionSafeWidth = guideWidth * (1 - ACTION_SAFE_MARGIN * 2);
  const actionSafeHeight = guideHeight * (1 - ACTION_SAFE_MARGIN * 2);
  const actionSafeOffsetX = offsetX + guideWidth * ACTION_SAFE_MARGIN;
  const actionSafeOffsetY = offsetY + guideHeight * ACTION_SAFE_MARGIN;

  const titleSafeWidth = guideWidth * (1 - TITLE_SAFE_MARGIN * 2);
  const titleSafeHeight = guideHeight * (1 - TITLE_SAFE_MARGIN * 2);
  const titleSafeOffsetX = offsetX + guideWidth * TITLE_SAFE_MARGIN;
  const titleSafeOffsetY = offsetY + guideHeight * TITLE_SAFE_MARGIN;

  if (videoGuideMaskEnabled) {
    return (
      <div ref={containerRef} className="pointer-events-none absolute inset-0">
        {offsetY > 0 && (
          <div
            className="absolute top-0 right-0 left-0 bg-black"
            style={{ height: offsetY }}
          />
        )}
        {offsetY > 0 && (
          <div
            className="absolute right-0 bottom-0 left-0 bg-black"
            style={{ height: offsetY }}
          />
        )}
        {offsetX > 0 && (
          <div
            className="absolute top-0 bottom-0 left-0 bg-black"
            style={{ width: offsetX, top: offsetY, bottom: offsetY }}
          />
        )}
        {offsetX > 0 && (
          <div
            className="absolute top-0 right-0 bottom-0 bg-black"
            style={{ width: offsetX, top: offsetY, bottom: offsetY }}
          />
        )}
        <div
          className={classNames(
            "absolute border-2 border-brand-400/80",
            "shadow-[0_0_0_1px_rgba(0,0,0,0.4)]",
          )}
          style={{
            left: actionSafeOffsetX,
            top: actionSafeOffsetY,
            width: actionSafeWidth,
            height: actionSafeHeight,
          }}
        />
        <div
          className={classNames(
            "absolute border-2 border-brand-400/80",
            "shadow-[0_0_0_1px_rgba(0,0,0,0.4)]",
          )}
          style={{
            left: titleSafeOffsetX,
            top: titleSafeOffsetY,
            width: titleSafeWidth,
            height: titleSafeHeight,
          }}
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="pointer-events-none absolute inset-0">
      <div
        className={classNames(
          "absolute border-2 border-brand-400/90",
          "shadow-[0_0_0_1px_rgba(0,0,0,0.5)]",
        )}
        style={{
          left: actionSafeOffsetX,
          top: actionSafeOffsetY,
          width: actionSafeWidth,
          height: actionSafeHeight,
        }}
      />
      <div
        className={classNames(
          "absolute border-2 border-brand-400/90",
          "shadow-[0_0_0_1px_rgba(0,0,0,0.5)]",
        )}
        style={{
          left: titleSafeOffsetX,
          top: titleSafeOffsetY,
          width: titleSafeWidth,
          height: titleSafeHeight,
        }}
      />
    </div>
  );
}

export default AspectRatioGuideOverlay;
